// Multi-currency support. Prices are STORED in MAD (the base). Display
// conversion is purely cosmetic — EUR/USD are shown as INDICATIVE figures
// (prefixed "≈ " and rounded to the nearest 10). The canonical MAD value is
// what the seller transacts on; nothing here ever mutates a stored price.

export type Currency = 'MAD' | 'EUR' | 'USD';

export const CURRENCIES: readonly Currency[] = ['MAD', 'EUR', 'USD'] as const;

export type FxRates = Record<Currency, number>;

// Static seed defaults — used in mock mode, before the first /api/fx refresh
// lands, or whenever the fetch fails. Mirrors the backend seed exactly so the
// UI never shows a wildly wrong indicative price.
export const SEED_RATES: FxRates = { MAD: 1, EUR: 0.0936, USD: 0.1074 };

export interface FxResult {
  rates: FxRates;
  updatedAt?: string;
  stale?: boolean;
}

interface FxResponse {
  base: string;
  rates: Partial<Record<Currency, number>>;
  updatedAt?: string;
  stale?: boolean;
}

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';
const baseUrl = import.meta.env.VITE_API_BASE_URL;

function isFiniteRate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Normalises an upstream/backend rate map into a complete {@link FxRates},
 * falling back to {@link SEED_RATES} for any missing or invalid currency so
 * the consumer always gets a usable number for MAD/EUR/USD.
 */
function coerceRates(raw: Partial<Record<Currency, number>> | undefined): FxRates {
  return {
    MAD: 1, // base is always exactly 1 by definition
    EUR: isFiniteRate(raw?.EUR) ? (raw!.EUR as number) : SEED_RATES.EUR,
    USD: isFiniteRate(raw?.USD) ? (raw!.USD as number) : SEED_RATES.USD,
  };
}

/**
 * Fetches FX rates from the public GET /api/fx endpoint (same-origin via the
 * /api proxy). In mock mode, or on ANY failure (network, non-2xx, parse), it
 * resolves to the static seed defaults rather than throwing — the storefront
 * must always have sane numbers to render.
 */
export async function fetchFxRates(): Promise<FxResult> {
  if (useMocks || !baseUrl) {
    return { rates: SEED_RATES, stale: true };
  }
  try {
    const response = await fetch(`${baseUrl}/fx`);
    if (!response.ok) {
      return { rates: SEED_RATES, stale: true };
    }
    const body = (await response.json()) as FxResponse;
    return {
      rates: coerceRates(body?.rates),
      updatedAt: body?.updatedAt,
      stale: body?.stale,
    };
  } catch {
    // Network error / parse error — fall back silently, no console spam.
    return { rates: SEED_RATES, stale: true };
  }
}

const LOCALE_BY_CURRENCY: Record<Currency, string> = {
  MAD: 'fr-MA',
  EUR: 'fr-FR',
  USD: 'en-US',
};

/** True for any Arabic UI language tag (ar, ar-MA, …). */
function isArabic(lang?: string): boolean {
  return typeof lang === 'string' && lang.toLowerCase().startsWith('ar');
}

/**
 * Formats a MAD-stored amount for display in the chosen currency.
 *
 *   - MAD  → exact, e.g. "1 490 MAD".
 *   - EUR/USD → INDICATIVE: convert (amountMad * rates[currency]), round to
 *     the nearest 10, prefix "≈ ", and format with the right symbol.
 */
export function formatPrice(
  amountMad: number,
  currency: Currency,
  rates: FxRates,
  lang?: string,
): string {
  if (currency === 'MAD') {
    return formatMad(amountMad, lang);
  }
  const rate = isFiniteRate(rates?.[currency]) ? rates[currency] : SEED_RATES[currency];
  const converted = amountMad * rate;
  const rounded = Math.round(converted / 10) * 10;
  const formatted = new Intl.NumberFormat(LOCALE_BY_CURRENCY[currency], {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rounded);
  return `≈ ${formatted}`;
}

/**
 * Canonical MAD formatter — used at the call sites that must stay in MAD
 * regardless of the selected display currency (WhatsApp reserve message,
 * Instagram story price).
 *
 *   - default  → "1 490 MAD" (Intl currency, fr-MA).
 *   - Arabic   → "1 490 درهم" — Western digits (Moroccan e-commerce norm) with
 *     the Arabic word. The number is a Latin (LTR) run; inside the RTL price
 *     element it sits to the right of درهم and reads naturally.
 */
export function formatMad(amountMad: number, lang?: string): string {
  if (isArabic(lang)) {
    const num = new Intl.NumberFormat('fr-MA', { maximumFractionDigits: 0 }).format(amountMad);
    return `${num} درهم`;
  }
  return new Intl.NumberFormat(LOCALE_BY_CURRENCY.MAD, {
    style: 'currency',
    currency: 'MAD',
    maximumFractionDigits: 0,
  }).format(amountMad);
}
